(() => {
  'use strict';

  window.ArtboxNavigationPanels?.destroy();

  const names = new Map([
    ['t4s-search-hidden', 'search'],
    ['t4s-login-sidebar', 'account'],
    ['t4s-mini_cart', 'bag'],
  ]);
  const root = document.documentElement;
  let active = 'none';
  let lastTrigger = null;
  let requested = null;
  let observer = null;
  let frame = 0;
  let closeTimer = 0;
  let hoverCloseTimer = 0;
  let initialised = false;

  function drawers() {
    return [...names].map(([id, name]) => ({ name, node: document.getElementById(id) }))
      .filter(({ node }) => node);
  }

  function closeDrawer(node) {
    if (!node || node.getAttribute('aria-hidden') !== 'false') return;
    window.T4SThemeSP?.Drawer?.close?.();
    if (node.getAttribute('aria-hidden') !== 'false') return;
    const close = node.querySelector('[data-drawer-close]');
    if (close) close.click();
    else node.setAttribute('aria-hidden', 'true');
  }

  function panelFromTrigger(target) {
    const trigger = target.closest?.('[data-drawer-options]');
    if (!trigger) return null;
    const options = trigger.getAttribute('data-drawer-options') || '';
    const id = options.match(/#(?:t4s-search-hidden|t4s-login-sidebar|t4s-mini_cart)/)?.[0]?.slice(1);
    return id ? { name: names.get(id), trigger } : null;
  }

  function freezeHeaderOffset() {
    if (!window.matchMedia('(max-width: 1024px)').matches) return;
    const navigation = document.querySelector('[data-artbox-navigation]');
    if (!navigation) return;
    root.style.setProperty('--artbox-panel-header-bottom', `${Math.round(navigation.getBoundingClientRect().height)}px`);
  }

  function sync() {
    frame = 0;
    const open = drawers().filter(({ node }) => node.getAttribute('aria-hidden') === 'false');
    if (requested && !open.some(({ name }) => name === requested)) return;
    const selected = open.find(({ name }) => name === requested) || open.at(-1);
    open.forEach(({ node }) => {
      if (node !== selected?.node) closeDrawer(node);
    });
    const next = selected?.name || 'none';
    requested = null;
    if (next === active) return;
    const previous = active;
    active = next;
    window.clearTimeout(closeTimer);
    if (next === 'none' && previous !== 'none') {
      root.dataset.artboxPanelClosing = previous;
      closeTimer = window.setTimeout(() => {
        delete root.dataset.artboxPanelClosing;
        closeTimer = 0;
      }, 260);
    } else {
      delete root.dataset.artboxPanelClosing;
    }
    root.dataset.artboxActivePanel = next;
    if ((next === 'search' || next === 'bag') && next !== previous) freezeHeaderOffset();
    document.querySelectorAll('[data-artbox-navigation] [data-drawer-options]').forEach((trigger) => {
      const panel = panelFromTrigger(trigger);
      if (!panel || (panel.name !== 'search' && panel.name !== 'bag')) return;
      const open = panel.name === next;
      trigger.setAttribute('aria-expanded', String(open));
      trigger.setAttribute('aria-label', open ? `Close ${panel.name === 'bag' ? 'Bag' : 'Search'}` : panel.name === 'bag' ? 'Bag' : 'Search');
    });
    document.dispatchEvent(new CustomEvent('artbox:panel:change', { detail: { panel: next, previous } }));
    if (next === 'none' && lastTrigger?.isConnected && lastTrigger.matches(':focus-visible')) {
      lastTrigger.focus({ preventScroll: true });
    }
  }

  function scheduleSync() {
    if (frame) return;
    frame = 1;
    queueMicrotask(() => {
      if (!initialised) {
        frame = 0;
        return;
      }
      sync();
    });
  }

  function onClick(event) {
    const panel = panelFromTrigger(event.target);
    if (!panel) return;
    window.ArtboxNavigation?.refresh?.();
    const current = drawers().find(({ name }) => name === panel.name)?.node;
    if (current?.getAttribute('aria-hidden') === 'false') {
      event.preventDefault();
      event.stopImmediatePropagation();
      root.dataset.artboxActivePanel = 'none';
      root.dataset.artboxPanelClosing = panel.name;
      requested = null;
      closeDrawer(current);
      scheduleSync();
      return;
    }
    root.dataset.artboxActivePanel = panel.name;
    freezeHeaderOffset();
    delete root.dataset.artboxPanelClosing;
    requested = panel.name;
    lastTrigger = panel.trigger;
    drawers().forEach(({ name, node }) => {
      if (name !== panel.name) closeDrawer(node);
    });
    scheduleSync();
  }

  function desktopPointer() {
    return window.matchMedia('(min-width: 1025px) and (hover: hover) and (pointer: fine)').matches;
  }

  function scheduleHoverClose(panel) {
    if (hoverCloseTimer) return;
    hoverCloseTimer = window.setTimeout(() => {
      hoverCloseTimer = 0;
      if (root.dataset.artboxCheckoutOverlay === 'open') return;
      closeDrawer(panel);
      scheduleSync();
    }, 80);
  }

  function onPointerOut(event) {
    if (event.pointerType !== 'mouse' || !desktopPointer()) return;
    const panel = event.target.closest?.('#t4s-search-hidden, #t4s-login-sidebar');
    if (!panel || panel.contains(event.relatedTarget)) return;
    if (panel.getAttribute('aria-hidden') !== 'false') return;
    scheduleHoverClose(panel);
  }

  function onPointerMove(event) {
    if (event.pointerType !== 'mouse' || !desktopPointer()) return;
    if (active !== 'search' && active !== 'account') return;
    const panel = drawers().find(({ name }) => name === active)?.node;
    if (!panel || panel.getAttribute('aria-hidden') !== 'false') return;
    if (event.clientY > panel.getBoundingClientRect().bottom + 2) scheduleHoverClose(panel);
  }

  function onPointerOver(event) {
    if (!event.target.closest?.('#t4s-search-hidden, #t4s-login-sidebar')) return;
    window.clearTimeout(hoverCloseTimer);
    hoverCloseTimer = 0;
  }

  function onMenuOpening() {
    if (root.dataset.artboxCheckoutOverlay === 'open') return;
    window.clearTimeout(hoverCloseTimer);
    hoverCloseTimer = 0;
    requested = null;
    drawers().forEach(({ node }) => closeDrawer(node));
    scheduleSync();
  }

  function onKeydown(event) {
    if (event.key !== 'Escape' || active === 'none') return;
    if (root.dataset.artboxCheckoutOverlay === 'open') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    closeActive();
    lastTrigger?.focus?.({ preventScroll: true });
  }

  function closeActive() {
    const current = drawers().find(({ name }) => name === active);
    closeDrawer(current?.node);
    scheduleSync();
  }

  function initialise() {
    if (initialised) return;
    initialised = true;
    root.dataset.artboxActivePanel = 'none';
    document.addEventListener('click', onClick, true);
    document.addEventListener('pointerout', onPointerOut, true);
    document.addEventListener('pointermove', onPointerMove, true);
    document.addEventListener('pointerover', onPointerOver, true);
    document.addEventListener('artbox:menu:opening', onMenuOpening);
    document.addEventListener('keydown', onKeydown, true);
    document.addEventListener('cart:updated', scheduleSync);
    observer = new MutationObserver(scheduleSync);
    observer.observe(document.body, { attributes: true, attributeFilter: ['aria-hidden'], childList: true, subtree: true });
    scheduleSync();
  }

  function destroy() {
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('pointerout', onPointerOut, true);
    document.removeEventListener('pointermove', onPointerMove, true);
    document.removeEventListener('pointerover', onPointerOver, true);
    document.removeEventListener('artbox:menu:opening', onMenuOpening);
    document.removeEventListener('keydown', onKeydown, true);
    document.removeEventListener('cart:updated', scheduleSync);
    observer?.disconnect();
    observer = null;
    window.clearTimeout(closeTimer);
    window.clearTimeout(hoverCloseTimer);
    frame = 0;
    closeTimer = 0;
    hoverCloseTimer = 0;
    initialised = false;
    delete root.dataset.artboxActivePanel;
    delete root.dataset.artboxPanelClosing;
  }

  window.ArtboxNavigationPanels = { initialise, destroy, closeActive };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialise, { once: true });
  else initialise();
})();
